import type { MetaFunction } from '@remix-run/node';
import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import type { SitemapFunction } from 'remix-sitemap';
import BlogPostPage from '~/components/BlogPostPage';

import type { PerfectWikiPublicPage } from '~/services/loadPage';
import { loadPageByHash } from '~/services/loadPage';

const pageSlugToHash: { [id: string]: string } = {
    'best-copilot-prompts-for-powerpoint': 'FwXk2IK1lx',
    'best-copilot-hr-prompts-for-word': 'dWsna7vy-9',
    'top-copilot-prompts-for-outlook': '-DMCwt3iI9',
    'top-copilot-prompts-for-excel': '49x-3m-_pa',
    'most-efficient-copilot-prompts-for-one-note': 'qS0f2m_RpN',
    'top-copilot-marketing-prompts-for-word': 'yBEREUbaKu',
    'top-copilot-retail-prompts-for-word': 'jCet72bopN',
    'top-copilot-manufacturing-prompts-for-word': 'SviipZGNmF',
    'top-copilot-prompts-for-word-to-master-finance': 'eyJhEuHzBT',
};

export const sitemap: SitemapFunction = ({ config, request }) => {
    return Object.keys(pageSlugToHash).map((key) => ({
        loc: `/copilot/${key}`,
        lastmod: new Date().toISOString(),
        exclude: false,
    }));
};

export async function loader({ params }: LoaderFunctionArgs) {
    const slug = params.slug!;

    const hash: string = pageSlugToHash[slug];
    if (!hash) {
        throw new Response(`Can not find page by slug [${slug}]`, {
            status: 404,
        });
    }

    const page = await loadPageByHash(hash);
    return json({ page });
}

export function generatePageMeta({
    title,
    description,
}: {
    title: string;
    description: string;
}) {
    const defaultMeta = [
        {
            title: title,
        },
        {
            description: description,
        },
        {
            property: 'og:type',
            content: 'article',
        },

        {
            property: 'og:description',
            content: description,
        },
        {
            property: 'og:title',
            content: title,
        },
        {
            property: 'og:image:alt',
            content: title,
        },
        {
            'script:ld+json': {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: title,
                author: [
                    {
                        '@type': 'Person',
                        name: 'Ilia',
                    },
                ],
                publisher: {
                    '@type': 'Organization',
                    name: 'AI Perfect Assistant',
                    url: 'https://perfectassistant.ai',
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://perfectassistant.ai/logo.png',
                        width: '100',
                        height: '100',
                    },
                },
            },
        },
    ];

    return defaultMeta;
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data) {
        return [];
    }

    const page = (data as any).page as PerfectWikiPublicPage;

    return generatePageMeta({
        title: page.title,
        description: page.description,
    });
};

export default function () {
    const data = useLoaderData();

    if (!data) {
        throw new Error('Loader data is undefined');
    }

    const page = (data as any).page as PerfectWikiPublicPage;

    return (
        <BlogPostPage
            updatedDateTimeIsoString={page.changed}
            title={page.title}
            htmlContent={page.content}
        />
    );
}
